import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

( function( app ) {
  const scrollTriggers = [];
  const inactiveState = {opacity: 0, pointerEvents: 'none'};
  const activeState = {opacity: 1, pointerEvents: 'auto'};

  let lastOffset = 0;
  let topOffsetMeasure;

  const getTopOffset = () => {
    const header = document.querySelector( '.navigation-one' );

    if ( header ) {
      return Math.round( header.getBoundingClientRect().bottom );
    }

    if ( !topOffsetMeasure ) {
      topOffsetMeasure = document.createElement( 'div' );
      topOffsetMeasure.setAttribute( 'aria-hidden', 'true' );
      topOffsetMeasure.style.cssText = [
        'position:absolute',
        'visibility:hidden',
        'pointer-events:none',
        'height:var(--topOffset)',
        'width:0',
        'overflow:hidden',
      ].join( ';' );
      document.body.appendChild( topOffsetMeasure );
    }

    return Math.round( topOffsetMeasure.getBoundingClientRect().height );
  };

  let offsetRefreshTimer;

  const refreshPinnedSections = () => {
    clearTimeout( offsetRefreshTimer );

    offsetRefreshTimer = setTimeout( () => {
      setPinnedHeights();

      scrollTriggers.forEach( ( trigger ) => {
        if ( trigger && trigger.pin ) {
          trigger.refresh();
        }
      } );

      ScrollTrigger.refresh( true );
    }, 50 );
  };

  const getPinnedHeight = () => Math.max( window.innerHeight - getTopOffset(), 0 );

  const setPinnedHeights = () => {
    document.querySelectorAll( '.scroll-seq' ).forEach( ( section ) => {
      section.style.minHeight = `${getPinnedHeight()}px`;
    } );
  };

  const afterInitialPaint = ( callback ) => {
    const run = () => {
      requestAnimationFrame( () => {
        requestAnimationFrame( callback );
      } );
    };

    const runWhenIdle = () => {
      if ( 'requestIdleCallback' in window ) {
        window.requestIdleCallback( run, {timeout: 1200} );
      } else {
        window.setTimeout( run, 600 );
      }
    };

    if ( document.readyState === 'complete' ) {
      runWhenIdle();
    } else {
      window.addEventListener( 'load', runWhenIdle, {once: true} );
    }
  };

  const watchHeaderOffset = () => {
    const check = () => {
      const current = getTopOffset();

      if ( current !== lastOffset ) {
        lastOffset = current;
        refreshPinnedSections();
      }
    };

    const loop = () => {
      check();
      requestAnimationFrame( loop );
    };

    loop();
  };

  const container = document.querySelector( '[data-component="scrolling-experience"]' );
  if ( !container ) return;

  setPinnedHeights();
  lastOffset = getTopOffset();

  const COMPONENT = {
    init: function() {
      afterInitialPaint( () => {
        this.initScrollExperience();
      } );
    },

    finalize: function() {},

    initDecorativeMotion: function() {
      const layers = document.querySelectorAll( '.layer' );

      layers.forEach( ( layer, i ) => {
        gsap.to( layer, {
          rotation: ( i % 2 ? -1 : 1 ) * ( 1 + i * 0.5 ),
          transformOrigin: '50% 50%',
          duration: 10 + i * 3,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        } );

        gsap.to( layer, {
          x: ( Math.random() - 0.5 ) * 6,
          y: ( Math.random() - 0.5 ) * 6,
          duration: 12 + i * 2,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        } );

        gsap.to( layer, {
          opacity: 0.4 + i * 0.15,
          duration: 5 + i,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        } );
      } );

      gsap.to( '#circleScene', {
        rotation: 360,
        duration: 180,
        ease: 'none',
        repeat: -1,
      } );
    },

    initScrollExperience: function() {
      const container = document.querySelector( '[data-component="scrolling-experience"]' );
      if ( !container ) return;

      setPinnedHeights();

      const hasVideoToFadingTransition = this.initVideoToFadingTimeline();

      if ( !hasVideoToFadingTransition ) {
        this.initVideoTextTimeline();
        this.initFadingTextTimeline();
      }

      this.initClockwiseSvgTimeline();
      this.initFooterOverlayTimeline();

      afterInitialPaint( () => {
        this.initDecorativeMotion();
      } );

      // initial safe refresh
      requestAnimationFrame( () => {
        setTimeout( () => {
          ScrollTrigger.refresh( true );
        }, 80 );
      } );

      watchHeaderOffset();

      let resizeTimer;

      window.addEventListener( 'resize', () => {
        clearTimeout( resizeTimer );

        resizeTimer = setTimeout( () => {
          setPinnedHeights();

          ScrollTrigger.refresh( true );
        }, 250 );
      } );
    },

    initVideoToFadingTimeline: function() {
      const videoSection = document.querySelector( '.scroll-seq--video' );
      const fadingSection = document.querySelector( '.scroll-seq--fading-text' );

      if ( !videoSection || !fadingSection ) return false;

      const videoIndex = Number( videoSection.dataset.seqIndex );
      const fadingIndex = Number( fadingSection.dataset.seqIndex );

      if ( fadingIndex !== videoIndex + 1 ) return false;

      videoSection.appendChild( fadingSection );

      const videoBackground = videoSection.querySelector( ':scope > .scroll-seq__background-pin' );
      const videoForeground = videoSection.querySelector( ':scope > .scroll-seq__foreground' );
      const videoContent = [videoBackground, videoForeground].filter( Boolean );
      const heading = videoSection.querySelector(
          '.scroll-seq__h1-stage h1, .scroll-seq__h1-stage h2, .scroll-seq__h1-stage .js-fade',
      );
      const textSteps = videoSection.querySelectorAll( '.scroll-seq__text-step' );
      const hintText = videoSection.querySelector( '.scroll-seq__hint' );

      const breathingScene = fadingSection.querySelector( '#breathingScene' );
      const s4 = fadingSection.querySelector( '.slide-4' );
      const s5 = fadingSection.querySelector( '.slide-5' );
      const s6 = fadingSection.querySelector( '.slide-6' );
      const s6Heading = fadingSection.querySelector( '.scroll-seq__s6-heading' );
      const mergedHeading = fadingSection.querySelector( '.scroll-seq__merged-heading' );
      const mergedSteps = fadingSection.querySelectorAll( '.scroll-seq__merged-step-slide' );

      gsap.set( textSteps, {...inactiveState, y: 40} );
      gsap.set( textSteps[0], {...activeState, y: 0} );
      gsap.set( fadingSection, {
        autoAlpha: 0,
        bottom: 0,
        height: '100%',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 30,
        pointerEvents: 'none',
      } );
      gsap.set( videoContent, {autoAlpha: 1, pointerEvents: 'auto'} );
      gsap.set( [s4, s5, s6], {...inactiveState, y: 40} );

      if ( s6Heading ) gsap.set( s6Heading, {...inactiveState, y: 40} );
      if ( breathingScene ) gsap.set( breathingScene, {...inactiveState, scale: 1} );
      if ( mergedHeading ) gsap.set( mergedHeading, {...inactiveState, y: 40} );

      mergedSteps.forEach( ( step, idx ) => {
        const icon = step.querySelector( '.scroll-seq__icon-wrapper' );
        const content = step.querySelector( '.scroll-seq__step-content' );
        const isLastStep = idx === mergedSteps.length - 1;

        gsap.set( step, inactiveState );
        if ( icon ) {
          gsap.set( icon, {
            ...inactiveState,
            scale: isLastStep ? 1 : 0.7,
            y: isLastStep ? 0 : 40,
          } );
        }
        if ( content ) gsap.set( content, {...inactiveState, y: isLastStep ? 0 : 40} );
      } );

      const tl = gsap.timeline( {
        scrollTrigger: {
          trigger: videoSection,
          start: () => `top top+=${getTopOffset()}`,
          end: '+=1100%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          pinSpacing: true,
        },
      } );

      tl.to( heading, {opacity: 0, y: -40, duration: 1} )
          .to( textSteps[0], {...inactiveState, y: -40, duration: 1}, '<' )
          .to( textSteps[1], {...activeState, y: 0, duration: 1}, '-=0.2' )
          .to( textSteps[1], {...inactiveState, y: -40, duration: 1, delay: 0.8} )
          .to( textSteps[2], {...activeState, y: 0, duration: 1}, '-=0.2' )
          .to( hintText, {...inactiveState, y: 15, duration: 0.5}, '-=0.5' )
          .to( videoContent, {autoAlpha: 0, pointerEvents: 'none', duration: 1}, '+=0.2' )
          .to( fadingSection, {autoAlpha: 1, pointerEvents: 'auto', duration: 1}, '<' );

      if ( breathingScene ) {
        tl.to( breathingScene, {...activeState, duration: 0.8}, '+=0.2' );
      }

      tl.to( s4, {...activeState, y: 0, duration: 1}, '+=0.2' )
          .to( s4, {...inactiveState, y: -40, duration: 1, delay: 1} );

      if ( breathingScene ) {
        tl.to( breathingScene, {...inactiveState, scale: 0.95, duration: 1}, '<' );
      }

      tl.to( s5, {...activeState, y: 0, duration: 1}, '+=0.1' )
          .to( s5, {...inactiveState, y: -40, duration: 1, delay: 1} );

      tl.to( s6, {...activeState, y: 0, duration: 1}, '+=0.1' );

      if ( s6Heading ) {
        tl.to( s6Heading, {...activeState, y: 0, duration: 0.8}, '<' );
      }

      tl.to( s6, {...inactiveState, y: -40, duration: 1, delay: 1} );

      if ( mergedHeading || mergedSteps.length > 0 ) {
        tl.to( mergedHeading, {...activeState, y: 0, duration: 1}, '+=0.2' );

        mergedSteps.forEach( ( step, idx ) => {
          const icon = step.querySelector( '.scroll-seq__icon-wrapper' );
          const content = step.querySelector( '.scroll-seq__step-content' );
          const isLastStep = idx === mergedSteps.length - 1;
          const isBeforeLastStep = idx === mergedSteps.length - 2;
          const textSwapDuration = 0.35;

          if ( isLastStep ) {
            const swapStart = `merged-step-${idx}-text-swap`;

            tl.addLabel( swapStart, '<' );
            tl.set( step, activeState, swapStart );

            if ( content ) {
              tl.to( content, {...activeState, duration: textSwapDuration}, swapStart );
            }
          } else {
            tl.to( step, {...activeState, duration: 0.2}, '+=0.2' );

            if ( icon ) {
              tl.to( icon, {
                ...activeState,
                scale: 1,
                y: 0,
                duration: 1,
              } );
            }

            if ( content ) {
              tl.to( content, {
                ...activeState,
                y: 0,
                duration: 1,
              }, '+=1.5' );
            }
          }

          if ( idx < mergedSteps.length - 1 ) {
            if ( isBeforeLastStep ) {
              tl.to( content, {...inactiveState, duration: textSwapDuration}, '+=1.2' );
            } else {
              tl.to( step, {
                ...inactiveState,
                y: -40,
                duration: 1,
                delay: 1.2,
              } );
            }
          } else {
            tl.to( [step, mergedHeading], {
              ...inactiveState,
              duration: 1,
              delay: 1.2,
            } );
          }
        } );
      }

      tl.to( fadingSection, {autoAlpha: 0, pointerEvents: 'none', duration: 0.5}, '+=0.1' );

      scrollTriggers.push( tl.scrollTrigger );

      return true;
    },

    initVideoTextTimeline: function() {
      const section = document.querySelector( '.scroll-seq--video' );
      if ( !section ) return;

      const heading = section.querySelector(
          '.scroll-seq__h1-stage h1, .scroll-seq__h1-stage h2, .scroll-seq__h1-stage .js-fade',
      );

      const textSteps = section.querySelectorAll( '.scroll-seq__text-step' );
      const hintText = section.querySelector( '.scroll-seq__hint' );

      gsap.set( textSteps, {...inactiveState, y: 40} );
      gsap.set( textSteps[0], {...activeState, y: 0} );

      const tl = gsap.timeline( {
        scrollTrigger: {
          trigger: section,
          start: () => `top top+=${getTopOffset()}`,
          end: '+=300%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          pinSpacing: true,
        },
      } );

      tl.to( heading, {opacity: 0, y: -40, duration: 1} )
          .to( textSteps[0], {...inactiveState, y: -40, duration: 1}, '<' )
          .to( textSteps[1], {...activeState, y: 0, duration: 1}, '-=0.2' )
          .to( textSteps[1], {...inactiveState, y: -40, duration: 1, delay: 0.8} )
          .to( textSteps[2], {...activeState, y: 0, duration: 1}, '-=0.2' )
          .to( hintText, {...inactiveState, y: 15, duration: 0.5}, '-=0.5' );

      scrollTriggers.push( tl.scrollTrigger );
    },

    initFadingTextTimeline: function() {
      const section = document.querySelector( '.scroll-seq--fading-text' );
      if ( !section ) return;

      const breathingScene = section.querySelector( '#breathingScene' );
      const s4 = section.querySelector( '.slide-4' );
      const s5 = section.querySelector( '.slide-5' );
      const s6 = section.querySelector( '.slide-6' );
      const s6Heading = section.querySelector( '.scroll-seq__s6-heading' );

      const mergedHeading = section.querySelector( '.scroll-seq__merged-heading' );
      const mergedSteps = section.querySelectorAll( '.scroll-seq__merged-step-slide' );

      gsap.set( [s5, s6], {...inactiveState, y: 40} );
      gsap.set( s4, {...activeState, y: 0} );

      if ( s6Heading ) gsap.set( s6Heading, {...inactiveState, y: 40} );
      if ( breathingScene ) gsap.set( breathingScene, {...activeState, scale: 1} );
      if ( mergedHeading ) gsap.set( mergedHeading, {...inactiveState, y: 40} );

      mergedSteps.forEach( ( step, idx ) => {
        const icon = step.querySelector( '.scroll-seq__icon-wrapper' );
        const content = step.querySelector( '.scroll-seq__step-content' );
        const isLastStep = idx === mergedSteps.length - 1;

        gsap.set( step, inactiveState );
        if ( icon ) {
          gsap.set( icon, {
            ...inactiveState,
            scale: isLastStep ? 1 : 0.7,
            y: isLastStep ? 0 : 40,
          } );
        }
        if ( content ) gsap.set( content, {...inactiveState, y: isLastStep ? 0 : 40} );
      } );

      const tl = gsap.timeline( {
        scrollTrigger: {
          trigger: section,
          start: () => `top top+=${getTopOffset()}`,
          end: '+=800%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          pinSpacing: true,
        },
      } );

      tl.to( s4, {...inactiveState, y: -40, duration: 1} );

      if ( breathingScene ) {
        tl.to( breathingScene, {...inactiveState, scale: 0.95, duration: 1}, '+=0.1' );
      }

      tl.to( s5, {...activeState, y: 0, duration: 1}, '+=0.1' )
          .to( s5, {...inactiveState, y: -40, duration: 1, delay: 1} );

      tl.to( s6, {...activeState, y: 0, duration: 1}, '+=0.1' );

      if ( s6Heading ) {
        tl.to( s6Heading, {...activeState, y: 0, duration: 0.8}, '<' );
      }

      tl.to( s6, {...inactiveState, y: -40, duration: 1, delay: 1} );

      if ( mergedHeading || mergedSteps.length > 0 ) {
        tl.to( mergedHeading, {...activeState, y: 0, duration: 1}, '+=0.2' );

        mergedSteps.forEach( ( step, idx ) => {
          const icon = step.querySelector( '.scroll-seq__icon-wrapper' );
          const content = step.querySelector( '.scroll-seq__step-content' );
          const isLastStep = idx === mergedSteps.length - 1;
          const isBeforeLastStep = idx === mergedSteps.length - 2;
          const textSwapDuration = 0.35;

          if ( isLastStep ) {
            const swapStart = `merged-step-${idx}-text-swap`;

            tl.addLabel( swapStart, '<' );
            tl.set( step, activeState, swapStart );

            if ( content ) {
              tl.to( content, {...activeState, duration: textSwapDuration}, swapStart );
            }
          } else {
            tl.to( step, {...activeState, duration: 0.2}, '+=0.2' );

            if ( icon ) {
              tl.to( icon, {...activeState, y: 0, scale: 1, duration: 1} );
            }

            if ( content ) {
              tl.to( content, {...activeState, y: 0, duration: 1}, '+=1.5' );
            }
          }

          if ( idx < mergedSteps.length - 1 ) {
            if ( isBeforeLastStep ) {
              tl.to( content, {...inactiveState, duration: textSwapDuration}, '+=1.2' );
            } else {
              tl.to( step, {
                ...inactiveState,
                y: -40,
                duration: 1,
                delay: 1.2,
              } );
            }
          } else {
            tl.to( [step, mergedHeading], {
              ...inactiveState,
              duration: 1,
              delay: 1.2,
            } );
          }
        } );
      }

      scrollTriggers.push( tl.scrollTrigger );
    },

    initClockwiseSvgTimeline: function() {
      const section = document.querySelector( '.scroll-seq--clockwise-svg' );
      if ( !section ) return;

      const intro = section.querySelector( '.scroll-seq__diagram-intro' );
      const circle = section.querySelector( '#breathingScene' );
      const nodes = section.querySelectorAll( '.scroll-seq__phrase-node' );
      const count = nodes.length;

      if ( !count ) return;

      gsap.set( intro, {...inactiveState, y: 10} );
      gsap.set( circle, inactiveState );

      const positionNodes = () => {
        const isMobile = window.matchMedia( '(max-width: 768px)' ).matches;
        const sectionRect = section.getBoundingClientRect();
        const availableHeight = Math.min(
            sectionRect.height,
            Math.max( window.innerHeight - getTopOffset(), 0 ),
        );
        const nodeRects = Array.from( nodes ).map( ( node ) => node.getBoundingClientRect() );
        const maxNodeWidth = Math.max( ...nodeRects.map( ( rect ) => rect.width ), 0 );
        const maxNodeHeight = Math.max( ...nodeRects.map( ( rect ) => rect.height ), 0 );

        if ( isMobile ) {
          const mobileScale = window.innerWidth < 390 ? 0.82 : 0.9;

          const xDistance = Math.min(
              200,
              Math.max( 140, ( sectionRect.width - maxNodeWidth - 32 ) / 2 ),
          );

          const yDistance = Math.min(
              175,
              Math.max( 150, ( availableHeight - maxNodeHeight - 80 ) / 2 ),
          );

          const mobilePositions = [
            {x: -0.95, y: -0.45},
            {x: -0.45, y: -0.9},
            {x: 0.45, y: -0.9},
            {x: 0.95, y: -0.45},
            {x: 0.95, y: 0.45},
            {x: 0.45, y: 0.9},
            {x: -0.45, y: 0.9},
            {x: -0.95, y: 0.45},
          ];

          nodes.forEach( ( node, idx ) => {
            const position = mobilePositions[idx] || mobilePositions[mobilePositions.length - 1];

            gsap.set( node, {
              scale: mobileScale,
              x: Math.round( xDistance * position.x ),
              y: Math.round( yDistance * position.y ),
            } );
          } );

          return;
        }

        const xDistance = Math.min(
            320,
            Math.max( 220, ( sectionRect.width - maxNodeWidth - 96 ) / 2 ),
        );
        const yDistance = Math.min(
            275,
            Math.max( 90, ( availableHeight - maxNodeHeight - 120 ) / 2 ),
        );

        nodes.forEach( ( node, idx ) => {
          const anglePosition =
        ( idx * ( 360 / count ) - 90 ) * ( Math.PI / 180 );
          const xCoord = Math.round( xDistance * Math.cos( anglePosition ) );
          const yCoord = Math.round( yDistance * Math.sin( anglePosition ) );

          gsap.set( node, {
            scale: 1,
            x: xCoord,
            y: yCoord,
          } );
        } );
      };

      positionNodes();
      ScrollTrigger.addEventListener( 'refreshInit', positionNodes );

      const tl = gsap.timeline( {
        scrollTrigger: {
          trigger: section,
          start: () => `top top+=${getTopOffset()}`,
          end: '+=400%',
          pin: true,
          scrub: true,
          invalidateOnRefresh: true,
          pinSpacing: true,
        },
      } );

      tl.to( intro, {...activeState, y: 0, duration: 0.8} );
      tl.to( circle, activeState, '+=0.2' );

      nodes.forEach( ( node ) => {
        gsap.set( node, {
          ...inactiveState,
        } );

        tl.to( node, {
          ...activeState,
          duration: 0.75,
        }, '+=0.15' );
      } );

      tl.to( {}, {duration: 1} );

      scrollTriggers.push( tl.scrollTrigger );
    },

    initFooterOverlayTimeline: function() {
      const section = document.querySelector( '.scroll-seq--footer' );
      if ( !section ) return;

      const background = section.querySelector( '.scroll-seq__footer-bg' );
      const overlay = section.querySelector( '.scroll-seq__footer-overlay' );
      const icons = section.querySelectorAll( '.scroll-seq__fading-icon svg' );
      const content = section.querySelector( '.scroll-seq__footer-wysiwyg' );
      const bottomLogo = section.querySelector( '.scroll-seq__footer-logo' );

      const tl = gsap.timeline( {
        scrollTrigger: {
          trigger: section,
          start: () => `top top+=${getTopOffset()}`,
          end: '+=200%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          pinSpacing: true,
        },
      } );

      tl.set( overlay, inactiveState )
          .fromTo( background, {...inactiveState, scale: 1.05}, {...activeState, scale: 1, duration: 1.2} )
          .fromTo( overlay, inactiveState, {...activeState, duration: 0.8}, '+=0.15' )
          .fromTo( icons, {...inactiveState, y: 30}, {...activeState, y: 0, stagger: 0.15, duration: 1.2}, '+=0.15' )
          .fromTo( content, {...inactiveState, y: 40}, {...activeState, y: 0, duration: 0.9}, '-=0.25' )
          .fromTo( bottomLogo, {...inactiveState, scale: 0.9}, {...activeState, scale: 1, duration: 0.8}, '-=0.15' );

      scrollTriggers.push( tl.scrollTrigger );
    },
  };

  app.registerComponent( 'scrolling-experience', COMPONENT );
} )( app );
